import { AdvancedMarker, AdvancedMarkerAnchorPoint } from '@vis.gl/react-google-maps'
import { ReactElement, useCallback, useState } from 'react'
import styled from 'styled-components'

import { addLightToRgbColor, addOpacityToRgbColor } from 'utils/style'
import { CirclePinIcon } from 'components/Icons/CirclePinIcon'
import { PinIcon } from 'components/Icons/PinIcon'
import { IStopFeatureProps } from 'features/RoutesPlanning/types'
import { groupBy } from 'lodash'

const StopInfoWindowWrapper = styled.div`
  position: absolute;
  top: -5px;
  left: 50%;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0px 7px 7px 0px rgba(110, 136, 181, 0.2);
  background-color: #ffffff;
  z-index: 1;
  transform: translate(-50%, -100%);
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StopInfoWindowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  white-space: nowrap;
`

const InfoWindowStopName = styled.div`
  color: #28265d;
  font-size: 14px;
  font-weight: 500;
`

const InfoWindowStopAddress = styled.div`
  color: #717171;
  font-size: 12px;
  font-weight: 400;
  line-height: 130%;
`

const RoutesList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const RouteItem = styled.li`
  display: flex;
  gap: 8px;
  align-items: center;
`

const StopIndex = styled.div<{ bgColor: string; color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 21px;
  height: 21px;
  border-radius: 50%;
  background-color: ${({ bgColor }): string => bgColor};
  color: ${({ color }): string => color};
  font-size: 12px;
  font-weight: 500;
`

const RouteName = styled.div`
  color: #28265d;
  font-size: 14px;
  font-weight: 500;
`

interface IStopInfoWindowProps {
  routes: IStopFeatureProps['routes']
}

const StopInfoWindow = ({ routes }: IStopInfoWindowProps): ReactElement => {
  const routesGroupedByStop = groupBy(routes, 'stopId')
  return (
    <StopInfoWindowWrapper>
      {Object.values(routesGroupedByStop).map((routes, index) => {
        const { stopAddress, stopName } = routes[0]
        return (
          <StopInfoWindowContainer key={index}>
            <InfoWindowStopName>{stopName}</InfoWindowStopName>
            <InfoWindowStopAddress>{stopAddress}</InfoWindowStopAddress>
            <RoutesList>
              {routes.map((route) => {
                return (
                  <RouteItem key={route.id}>
                    <StopIndex bgColor={addOpacityToRgbColor(route.rgbColor, 0.125)} color={route.rgbColor}>
                      {route.isOrigin && 'D'}
                      {route.isDestination && 'A'}
                      {!route.isOrigin && !route.isDestination && route.stopIndex}
                    </StopIndex>
                    <RouteName>{route.name}</RouteName>
                  </RouteItem>
                )
              })}
            </RoutesList>
          </StopInfoWindowContainer>
        )
      })}
    </StopInfoWindowWrapper>
  )
}

const MarkerWrapper = styled.div`
  position: relative;
`

const Flex = styled.div`
  display: flex;
`

const IntermediateStop = styled.div<{ bgColor: string; isClicked: boolean; isHovered: boolean }>`
  width: ${({ isClicked }): string => (isClicked ? '22px' : '17px')};
  height: ${({ isClicked }): string => (isClicked ? '22px' : '17px')};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #ffffff;
  background-color: ${({ bgColor }): string => bgColor};
  border: 2px solid #ffffff;
`

interface IFeatureMarkerProps {
  position: google.maps.LatLngLiteral
  rgbColor: string
  isOrigin: boolean
  isDestination: boolean
  stopIndex: number
  routes: IStopFeatureProps['routes']
}

export const FeatureMarker = ({
  position,
  rgbColor,
  isOrigin,
  isDestination,
  stopIndex,
  routes
}: IFeatureMarkerProps): ReactElement => {
  let markerEl
  const [isHovered, setIsHovered] = useState(false)
  const [isClicked, setIsClicked] = useState(false)

  const handleMarkerClick = useCallback(() => setIsClicked((isShown: boolean) => !isShown), [])
  const handleMarkerMouseEnter = useCallback(() => setIsHovered(true), [])
  const handleMarkerMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  const bgColor = isHovered ? addLightToRgbColor(rgbColor, 0.15) : rgbColor
  if (isOrigin) {
    markerEl = (
      <Flex>
        <CirclePinIcon fillColor={bgColor} width={isClicked ? 40 : 32} height={isClicked ? 40 : 32} />
      </Flex>
    )
  } else if (isDestination) {
    markerEl = (
      <Flex>
        <PinIcon fillColor={bgColor} width={isClicked ? 27 : 22} height={isClicked ? 34 : 27} />
      </Flex>
    )
  } else {
    markerEl = (
      <IntermediateStop bgColor={bgColor} isClicked={isClicked} isHovered={isHovered}>
        {stopIndex}
      </IntermediateStop>
    )
  }

  return (
    <>
      <AdvancedMarker
        position={position}
        anchorPoint={isDestination ? undefined : AdvancedMarkerAnchorPoint.CENTER}
        onClick={handleMarkerClick}
        onMouseEnter={handleMarkerMouseEnter}
        onMouseLeave={handleMarkerMouseLeave}
        zIndex={isClicked ? 1000 : 0}
      >
        <MarkerWrapper>
          {markerEl}
          {isClicked && <StopInfoWindow routes={routes} />}
        </MarkerWrapper>
      </AdvancedMarker>
    </>
  )
}
